import { memo, useEffect, useState } from 'react';
import type { BillDTO, RecoveredResponse, RecoveryDTO } from '@six-pockets/shared';
import { billsApi } from '@/api';
import { EyeIcon } from '@/components/icons';
import { Avatar, Button, InlineAlert } from '@/components/ui';
import { cx } from '@/utils/cx';
import { formatDayLabel } from '@/utils/date';
import { formatClock, formatINR, formatPhone } from '@/utils/format';
import { billContents, billParty } from '../utils';
import styles from '@/styles/components/RecoveredView/style.scss';

const messageOf = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');
const shortDay = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

function howPaid(r: RecoveryDTO): string {
  if (r.method === 'split') return `UPI ${formatINR(r.upi)} + Cash ${formatINR(r.cash)}`;
  return r.method === 'upi' ? 'UPI' : 'Cash';
}

/** One Pay now: who, when, how, and what it put on each bill (oldest first) with what that bill owed after. */
const RecoveryCard = memo(function RecoveryCard({ recovery: r, onView }: { recovery: RecoveryDTO; onView: (bill: BillDTO) => void }) {
  const name = r.customer?.name ?? billParty(r.lines[0]!.bill);
  return (
    <li className={styles.card}>
      <div className={styles.head}>
        <Avatar name={name} tone={r.customer ? 'green' : 'neutral'} />
        <div className={styles.who}>
          <span className={styles.name}>{name}</span>
          <span className={styles.meta}>
            {r.customer && <span className={styles.phone}>{formatPhone(r.customer.phone)}</span>}
            <span>
              {formatClock(r.paidAt)} · {howPaid(r)}
            </span>
          </span>
        </div>
        <div className={styles.amount}>
          <span className={styles.amountLabel}>Recovered</span>
          <span className={styles.amountValue}>{formatINR(r.amount)}</span>
          {r.discount > 0 && <span className={styles.discount}>{formatINR(r.discount)} discount</span>}
        </div>
      </div>

      <ul className={styles.lines}>
        {r.lines.map((l) => (
          <li key={l.bill.id} className={styles.line}>
            <span className={styles.number}>#{l.bill.billNumber}</span>
            <span className={styles.billMain}>
              <span className={styles.billDay}>Billed {shortDay(l.bill.createdAt)}</span>
              <span className={styles.contents}>{billContents(l.bill)}</span>
            </span>
            <span className={styles.figures}>
              <span>
                Bill <b className={styles.total}>{formatINR(l.bill.total)}</b>
              </span>
              {/* This bill's share of the one payment above ("₹216 of ₹725"), so it doesn't read as separate payments. */}
              <span>
                Paid <b className={styles.paid}>{formatINR(l.amount)}</b>
                {(r.lines.length > 1 || r.paymentAmount !== l.amount) && <> of {formatINR(r.paymentAmount)}</>}
              </span>
              {l.discount > 0 && (
                <span>
                  Discount <b className={styles.discount}>{formatINR(l.discount)}</b>
                </span>
              )}
              <span className={cx(styles.effect, l.dueAfter === 0 ? styles.cleared : styles.left)}>
                {l.dueAfter === 0 ? 'Cleared' : `${formatINR(l.dueAfter)} left`}
              </span>
            </span>
            <Button size="sm" variant="secondary" icon={<EyeIcon size={15} />} onClick={() => onView(l.bill)} aria-label={`View bill ${l.bill.billNumber}`}>
              View
            </Button>
          </li>
        ))}
      </ul>
    </li>
  );
});

interface RecoveredViewProps {
  /** YYYY-MM-DD (the page holds the day picker, beside the tabs). */
  date: string;
  onView: (bill: BillDTO) => void;
}

/**
 * Balance bills → Recovered: every balance recovered (Pay now) on a day, newest first, with the totals by UPI and
 * cash, and for each one the bills it reached and what each still owed after.
 */
export function RecoveredView({ date, onView }: RecoveredViewProps) {
  const [result, setResult] = useState<RecoveredResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    billsApi.recovered({ date }, ctrl.signal).then(
      (r) => {
        setResult(r);
        setError(null);
        setLoading(false);
      },
      (err) => {
        if (ctrl.signal.aborted) return;
        setError(messageOf(err));
        setLoading(false);
      },
    );
    return () => ctrl.abort();
  }, [date]);

  const day = formatDayLabel(date);
  const n = result?.recoveries.length ?? 0;

  return (
    <>
      {result && (
        <section className={styles.summary} aria-label="Recovered">
          <div className={styles.recovered}>
            <span className={styles.statLabel}>Recovered {day === 'Today' || day === 'Yesterday' ? day.toLowerCase() : `on ${day}`}</span>
            <span className={styles.recoveredValue}>{formatINR(result.total)}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>UPI</span>
            <span className={styles.statValue}>{formatINR(result.upi)}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Cash</span>
            <span className={styles.statValue}>{formatINR(result.cash)}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Payments</span>
            <span className={styles.statValue}>{n}</span>
          </div>
        </section>
      )}

      {error && <InlineAlert message={error} onDismiss={() => setError(null)} />}

      {!result && loading ? (
        <p className={styles.empty}>Loading…</p>
      ) : n === 0 ? (
        <p className={styles.empty}>No balances recovered {day === 'Today' ? 'today' : `on ${day}`}</p>
      ) : (
        <ul className={styles.list} aria-busy={loading}>
          {result!.recoveries.map((r) => (
            <RecoveryCard key={r.paymentId} recovery={r} onView={onView} />
          ))}
        </ul>
      )}
    </>
  );
}
